/**
 * A session on the person's own computer as the /code client reads one: its
 * `SessionResponse`, built in the browser from the snapshot the page's own
 * client reads (`getSnapshot` in `operations.ts`). The cloud server builds
 * the same shape from cubic's rows; here every turn is read back from
 * OpenCode's messages and inbox.
 */
import type {
  LocalSessionSnapshot,
  LocalSessionSummary,
} from '@/lib/code-sessions/local-agent/operations';
import type { SavedDrain } from '@/lib/code-sessions/turn-utils';
import type { CodeSessionEvent } from '@/lib/code-sessions/types';
import type {
  PromptFileAttachment,
  SessionInboxCompaction,
  SessionInboxInfo,
  SessionInboxUser,
  SessionInfo,
  SessionMessageAssistant,
  SessionMessageCompaction,
  SessionMessageIdle,
  SessionMessageInfo,
  SessionMessageUser,
  ToolContent,
} from '@opencode/client/promise';

import type {
  CodePromptAttachment,
  CodeSessionRow,
  CodeSessionTurn,
  SessionResponse,
} from '../../types';

import { approvalForms } from '../../permission-form';
import { placeBySendTime } from '../unconfirmed-sends';

import { approvalModeOf } from '@/lib/code-sessions/approval-mode';
import { isImageAttachmentType } from '@/lib/code-sessions/attachment-types';
import { COMPACT_COMMAND } from '@/lib/code-sessions/compaction';
import {
  LOCAL_SESSION_IMAGE_BASE64_BYTES,
  localImageBase64Bytes,
} from '@/lib/code-sessions/image-limits';
import { archivedAt } from '@/lib/code-sessions/local-agent/archive-mark';
import { CODE_SESSION_TITLE_MAX_LENGTH, codeSessionPromptTitle } from '@/lib/code-sessions/title';
import {
  deterministicPromptId,
  savedTurnFinalText,
  savedTurnOutcome,
} from '@/lib/code-sessions/turn-utils';

/**
 * The one person on a local session: whoever sits at the computer. OpenCode
 * keeps no profile, and the transcript names authors only once two people
 * wrote, so a fixed id keeps every prompt theirs.
 */
export const LOCAL_SESSION_AUTHOR_ID = 'local-session-author';

/** A prompt message id the client named: the cloud's `msg_` plus the turn's uuid without dashes. */
const CLIENT_PROMPT_ID = /^msg_([0-9a-f]{8})([0-9a-f]{4})([0-9a-f]{4})([0-9a-f]{4})([0-9a-f]{12})$/;

/**
 * The OpenCode message id a turn's prompt is sent under, as the cloud derives
 * it from the turn id the client chose, so each turn row matches one message.
 * A turn read back from a message another client sent keeps that message's
 * own id as its turn id, and so sends under it.
 */
export function localPromptId(turnId: string): string {
  return turnId.startsWith('msg_') ? turnId : deterministicPromptId(turnId);
}

/** The turn id of a prompt message: the client's uuid when it named the message, else the message id. */
export function localTurnId(messageId: string): string {
  const match = CLIENT_PROMPT_ID.exec(messageId);
  return match === null ? messageId : `${match[1]}-${match[2]}-${match[3]}-${match[4]}-${match[5]}`;
}

export function localRewindTurnId(messageId: string | undefined): string | null {
  return messageId === undefined ? null : localTurnId(messageId);
}

export function isoTime(epochMs: number): string {
  return new Date(epochMs).toISOString();
}

export function folderName(folder: string): string {
  return folder.split('/').filter(Boolean).at(-1) ?? folder;
}

/**
 * What names a session OpenCode has not titled yet: its first prompt, or for images alone its
 * first image's name (`codeSessionPromptTitle`), cut as the cloud cuts the first prompt it names a
 * new session by until a title is generated (`generate-title.ts`). The row's label names the
 * folder already, so the folder names only a session with no prompt.
 */
export function untitledSessionName(folder: string, firstPrompt: string | undefined): string {
  return firstPrompt === undefined ? folder : firstPrompt.slice(0, CODE_SESSION_TITLE_MAX_LENGTH);
}

export type LocalTurnRow = CodeSessionTurn & {
  promptAttachments: readonly CodePromptAttachment[];
};

const TURN_ROW_CONSTANTS = {
  changedFiles: null,
  discardedAt: null,
  checkInReason: null,
  messageFrom: null,
  authorProfileId: LOCAL_SESSION_AUTHOR_ID,
} as const satisfies Partial<CodeSessionTurn>;

/** A message that starts a turn: a prompt, or a compaction the person asked for (`/compact`). */
type PromptMessage = SessionMessageUser | (SessionMessageCompaction & { reason: 'manual' });

/** OpenCode's own compactions run inside a turn, so they start none. */
function isPromptMessage(message: SessionMessageInfo): message is PromptMessage {
  return message.type === 'user' || (message.type === 'compaction' && message.reason === 'manual');
}

function isPromptInboxItem(
  item: SessionInboxInfo,
): item is SessionInboxUser | SessionInboxCompaction {
  return item.type === 'user' || item.type === 'compaction';
}

/** The bytes canonical base64 holds, as OpenCode stores a file. */
function decodedBytes(base64: string): number {
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return (base64.length / 4) * 3 - padding;
}

/**
 * The images among a prompt's files as the transcript shows them: from the copy OpenCode stores,
 * which it may have shrunk (`media.image` in the laptop config).
 */
function storedPromptImages(
  messageId: string,
  files: readonly PromptFileAttachment[] | undefined,
): CodePromptAttachment[] {
  return (files ?? []).flatMap((file, index) =>
    isImageAttachmentType(file.mime)
      ? [
          {
            id: `${messageId}:${index}`,
            name: file.name ?? 'image',
            contentType: file.mime,
            size: decodedBytes(file.data),
            dataUrl: `data:${file.mime};base64,${file.data}`,
          },
        ]
      : [],
  );
}

/**
 * The base64 bytes of images the session stores, which OpenCode sends again with every model
 * call, counted as OpenCode counts them (`boundImages` in
 * `packages/core/src/session/model-request.ts`): each prompt's images, delivered or waiting in the
 * inbox, and each image a tool returned, at the length of its `data:` URI. A prompt delivered
 * between the snapshot's two reads is in both lists, and counts once. The messages of a staged
 * Undo, from the undone one on, are left out: OpenCode deletes them with the next prompt.
 */
function storedImageBase64Bytes({
  messages,
  inbox,
  session,
}: Pick<LocalSessionSnapshot, 'messages' | 'inbox' | 'session'>): number {
  const delivered = new Set(messages.filter(isUserMessage).map((message) => message.id));
  const undoneAt = messages.findIndex((message) => message.id === session.revert?.messageID);
  const kept = undoneAt === -1 ? messages : messages.slice(0, undoneAt);
  const promptImages = [
    ...kept.filter(isUserMessage).flatMap((message) => message.files ?? []),
    ...inbox
      .filter((item): item is SessionInboxUser => item.type === 'user')
      .filter((item) => !delivered.has(item.id))
      .flatMap((item) => item.payload.files ?? []),
  ].reduce((sum, file) => sum + (isImageAttachmentType(file.mime) ? file.data.length : 0), 0);
  const toolImages = kept
    .filter(isAssistantMessage)
    .flatMap((message) => message.content)
    .flatMap((part): readonly ToolContent[] =>
      part.type === 'tool' && 'content' in part.state ? (part.state.content ?? []) : [],
    )
    .reduce(
      (sum, item) =>
        sum + (item.type === 'file' && isImageAttachmentType(item.mime) ? item.uri.length : 0),
      0,
    );
  return promptImages + toolImages;
}

/**
 * The base64 bytes of images that `turns` hold and the transcript does not count: those of a
 * session not yet read, from the turns its create answered with (its first prompt), and those of
 * sends OpenCode never got, which their Retry sends again. A read of the session gives
 * `storedImageBase64Bytes` the count of what OpenCode holds.
 */
function sentImageBase64Bytes(turns: readonly LocalTurnRow[]): number {
  return turns
    .flatMap((turn) => turn.promptAttachments)
    .reduce((sum, image) => sum + localImageBase64Bytes(image.size), 0);
}

/** The base64 bytes of images a session on the person's own computer can still store. */
export function localImageRoom(
  countedBytes: number,
  uncountedTurns: readonly LocalTurnRow[],
): number {
  return LOCAL_SESSION_IMAGE_BASE64_BYTES - countedBytes - sentImageBase64Bytes(uncountedTurns);
}

/**
 * How far apart OpenCode stamps the prompts it delivers together: it saves
 * them one after another as it delivers them, milliseconds apart.
 */
const DELIVERED_TOGETHER_MS = 1_000;

/** How soon after its marker OpenCode saves a cut-in's first steer: 45 to 56 ms on 2026-10-14. */
const CUT_IN_STEER_WITHIN_MS = 1_000;

/**
 * A cut-in's interrupt with `resume` (`steer-cut-in.ts`) saves an interrupted
 * idle marker, and OpenCode delivers the waiting steers in a new execution
 * milliseconds later, so the run goes on with them. After a plain Stop the
 * inbox stays parked until the person sends again, seconds later.
 */
function isCutInMarker(messages: readonly SessionMessageInfo[], at: number): boolean {
  const marker = messages[at];
  const next = messages[at + 1];
  return (
    marker?.type === 'idle' &&
    marker.outcome === 'interrupted' &&
    next !== undefined &&
    isPromptMessage(next) &&
    next.time.created - marker.time.created < CUT_IN_STEER_WITHIN_MS
  );
}

function runEnd(
  messages: readonly SessionMessageInfo[],
  at: number,
  end = messages.length,
): SessionMessageIdle | undefined {
  return messages
    .slice(at + 1, end)
    .find(
      (message, offset): message is SessionMessageIdle =>
        message.type === 'idle' && !isCutInMarker(messages, at + 1 + offset),
    );
}

/**
 * Whether the prompt at `at` was steered into the run before it. OpenCode
 * delivers every steer waiting at a step boundary together, one prompt after
 * another, so the reply just before the first of them ended with
 * `tool-calls`, the model expecting to go on. A queued follow-up is delivered
 * after a reply ended with `stop`, and so is a steer still waiting when the
 * model stopped: the run's work had ended, so it reads as a run of its own. A
 * prompt stamped well after the one before it was not delivered with it, as
 * one sent once a killed server started again, after a steer whose step never
 * began. A cut-in delivers its steers into the run right after its marker.
 */
function steeredIn(messages: readonly SessionMessageInfo[], at: number): boolean {
  let first = at;
  let before = messages[first - 1];
  // Back over the prompts delivered together with this one, to the message before them all.
  while (
    before !== undefined &&
    isPromptMessage(before) &&
    (messages[first] as PromptMessage).time.created - before.time.created < DELIVERED_TOGETHER_MS
  ) {
    first -= 1;
    before = messages[first - 1];
  }
  return (
    (before?.type === 'assistant' && before.finish === 'tool-calls') ||
    isCutInMarker(messages, first - 1)
  );
}

/**
 * The next prompt after the one at `index` that started a run of its own, or
 * none: a steer joined the run before it, so it is passed over, and its
 * replies are the run's work.
 */
function nextOwnPrompt(
  messages: readonly SessionMessageInfo[],
  prompts: readonly number[],
  index: number,
): number | undefined {
  let next = prompts[index + 1];
  for (let later = index + 1; next !== undefined && steeredIn(messages, next); later += 1) {
    next = prompts[later + 1];
  }
  return next;
}

/**
 * The saved messages between a prompt and the next one that started a run of
 * its own, or the end: the prompt's own period, steers delivered into its run
 * included, since their replies are the run's work.
 */
function promptPeriod(
  messages: readonly SessionMessageInfo[],
  at: number,
  prompts: readonly number[],
  index: number,
): SavedDrain {
  const end = nextOwnPrompt(messages, prompts, index) ?? messages.length;
  const own = messages.slice(at + 1, end);
  const idle = runEnd(messages, at, end);
  const prompt = messages[at] as PromptMessage;
  return {
    promptIds: [prompt.id],
    messages: idle === undefined ? own : own.slice(0, own.indexOf(idle)),
    idle,
  };
}

/**
 * Whether OpenCode cut this reply off. A graceful stop (stdin closed, as the
 * plugin stops the server, or SIGTERM) aborts the step and closes the reply
 * itself: `finish: 'error'` with the error aborted. A kill or a crash stops a
 * step with no end; when the next prompt starts, OpenCode completes the reply
 * with the step marked "Tool execution interrupted" and no finish reason.
 * Each is its own signal, read here instead of the order in which replies
 * completed.
 */
function cutOffReply(reply: SessionMessageAssistant): boolean {
  if (reply.finish === 'error') {
    return reply.error?.type === 'aborted';
  }
  return (
    reply.finish === undefined &&
    reply.content.some(
      (part) =>
        part.type === 'tool' &&
        part.state.status === 'error' &&
        part.state.error.message.startsWith('Tool execution interrupted'),
    )
  );
}

/**
 * When a cut-off turn ended. A stop closed the reply at the stop, so that
 * close is the end. A kill stored nothing about the stop, and the repair
 * closed the reply when the next prompt started, which says nothing about
 * the turn: it has no end to show.
 */
function cutOffEnd(reply: SessionMessageAssistant): number | null {
  return reply.finish === 'error' ? (reply.time.completed ?? null) : null;
}

/** When the prompt's own replies last completed, which is when its turn's work ended. */
function lastReplyEnd(period: SavedDrain): number | undefined {
  return period.messages
    .flatMap((message) =>
      message.type === 'assistant' && message.time.completed !== undefined
        ? [message.time.completed]
        : [],
    )
    .at(-1);
}

/**
 * The turn a delivered prompt message makes, read from its own period: the
 * messages from the prompt to the next one. An idle marker inside the period
 * ends the turn with that outcome. A cut-in's marker and the reply it cut off
 * end nothing (`isCutInMarker`): the run goes on with the steers it delivered.
 * Any other reply OpenCode cut off (`cutOffReply`),
 * or one never finished though a later prompt started a run of its own, ends
 * it interrupted; a steer delivered into the run is no such prompt, so a read
 * while the steered reply streams keeps the turn running. With
 * no marker of its own, the turn ended with the run that delivered it,
 * whose marker comes after a later prompt; while the session is still active
 * and nothing has ended, every prompt of the open run is running, as the
 * cloud shows them. A period with no end at all once the server no longer
 * lists the session as active was cut off by a quit or a crash. The turn's
 * time ends at its own last reply, not at the run's shared marker, and
 * never after its own marker.
 */
function deliveredTurn(
  messages: readonly SessionMessageInfo[],
  prompts: readonly number[],
  index: number,
  active: boolean,
): LocalTurnRow {
  const at = prompts[index]!;
  const next = nextOwnPrompt(messages, prompts, index);
  const message = messages[at] as PromptMessage;
  const period = promptPeriod(messages, at, prompts, index);
  const ownEnd = lastReplyEnd(period);
  const lastReply = period.messages.filter(isAssistantMessage).at(-1);
  const cutInEnded =
    lastReply !== undefined && isCutInMarker(messages, messages.indexOf(lastReply) + 1);
  const shared = runEnd(messages, at);
  let status: CodeSessionTurn['status'];
  let endedAt: string | null;
  if (period.idle !== undefined) {
    status = savedTurnOutcome(period) ?? 'done';
    // A reply closed after the turn's own marker was closed by a later repair, as OpenCode
    // closes a reply an interrupt left open when the next prompt starts: the turn ended at the
    // marker, not minutes later.
    const marker = period.idle.time.created;
    endedAt = isoTime(Math.min(ownEnd ?? marker, marker));
  } else if (lastReply !== undefined && !cutInEnded && cutOffReply(lastReply)) {
    status = 'interrupted';
    const end = cutOffEnd(lastReply);
    endedAt = end === null ? null : isoTime(end);
  } else if (
    lastReply !== undefined &&
    lastReply.time.completed === undefined &&
    next !== undefined
  ) {
    status = 'interrupted';
    endedAt = null;
  } else if (shared !== undefined) {
    status = savedTurnOutcome({ promptIds: [message.id], messages: [], idle: shared }) ?? 'done';
    endedAt = isoTime(ownEnd ?? shared.time.created);
  } else {
    status = active ? 'running' : 'interrupted';
    endedAt = null;
  }
  const createdAt = isoTime(message.time.created);
  return {
    ...TURN_ROW_CONSTANTS,
    id: localTurnId(message.id),
    // A steer joined the run before it; the fold keeps it there, as it did live.
    delivery: steeredIn(messages, at) ? 'steer' : 'queue',
    index,
    inboxId: message.id,
    status,
    prompt: message.type === 'user' ? message.text : COMPACT_COMMAND,
    createdAt,
    // OpenCode holds the prompt from the moment it is sent, so no edit or move can change it.
    admissionAttemptedAt: createdAt,
    startedAt: createdAt,
    endedAt,
    errorMessage:
      status === 'failed' ? (savedTurnFinalText(messages, message.id).error ?? null) : null,
    promptAttachments: message.type === 'user' ? storedPromptImages(message.id, message.files) : [],
  };
}

function isAssistantMessage(message: SessionMessageInfo): message is SessionMessageAssistant {
  return message.type === 'assistant';
}

function isUserMessage(message: SessionMessageInfo): message is SessionMessageUser {
  return message.type === 'user';
}

export function heldPromptImages(
  item: SessionInboxUser | SessionInboxCompaction | undefined,
  sent: readonly CodePromptAttachment[],
): readonly CodePromptAttachment[] {
  return item?.type === 'user' ? storedPromptImages(item.id, item.payload.files) : sent;
}

/** The turn a prompt waiting in the inbox makes: the row a send makes, as OpenCode holds it. */
function queuedTurn(item: SessionInboxUser | SessionInboxCompaction, index: number): LocalTurnRow {
  return sentTurn({
    turnId: localTurnId(item.id),
    messageId: item.id,
    text: item.type === 'user' ? item.payload.text : COMPACT_COMMAND,
    delivery: item.delivery,
    index,
    createdAt: item.time.created,
    attachments: heldPromptImages(item, []),
  });
}

/** A turn row as this client sends a prompt, before OpenCode delivers it. */
export function sentTurn(input: {
  turnId: string;
  messageId: string;
  text: string;
  delivery: CodeSessionTurn['delivery'];
  index: number;
  createdAt: number;
  attachments: readonly CodePromptAttachment[];
}): LocalTurnRow {
  return {
    ...TURN_ROW_CONSTANTS,
    id: input.turnId,
    delivery: input.delivery,
    index: input.index,
    inboxId: input.messageId,
    status: 'queued',
    prompt: input.text,
    createdAt: isoTime(input.createdAt),
    admissionAttemptedAt: isoTime(input.createdAt),
    startedAt: null,
    endedAt: null,
    errorMessage: null,
    promptAttachments: input.attachments,
  };
}

/**
 * The session's turns, oldest first: one per delivered prompt message, then
 * one per prompt still waiting in the inbox. Their index is their place here.
 * A compaction the person asked for is a prompt here, `/compact`, as they sent it.
 * OpenCode saves a notice (`system`) when a run starts with changed instructions,
 * as the run a cut-in resumes can, between the marker and the steer: the turns
 * read past it.
 */
export function localTurnRows(
  input: Pick<LocalSessionSnapshot, 'messages' | 'inbox' | 'active'>,
): LocalTurnRow[] {
  const messages = input.messages.filter((message) => message.type !== 'system');
  const prompts = messages.flatMap((message, at) => (isPromptMessage(message) ? [at] : []));
  const delivered = prompts.map((_, index) =>
    deliveredTurn(messages, prompts, index, input.active),
  );
  const queued = input.inbox
    .filter(isPromptInboxItem)
    .sort((a, b) => a.time.created - b.time.created)
    .map((item, offset) => queuedTurn(item, delivered.length + offset));
  return [...delivered, ...queued];
}

/**
 * A read's turns with the sends OpenCode never got among them, each ahead of
 * the first turn sent after it, and every turn numbered by its place.
 * OpenCode numbers only the prompts it holds, so it gives a prompt sent after
 * such a send that send's number, and the prompt would sort before it.
 */
export function withUnsentTurns(
  turns: LocalTurnRow[],
  unsent: readonly LocalTurnRow[],
): LocalTurnRow[] {
  if (unsent.length === 0) {
    return turns;
  }
  return placeBySendTime(turns, unsent).map((turn, index) =>
    turn.index === index ? turn : { ...turn, index },
  );
}

function modelName(session: SessionInfo): string {
  return session.model === undefined ? '' : `${session.model.providerID}/${session.model.id}`;
}

/**
 * When a session last ran, as the cloud dates a session by its work rather than by a rename. A
 * session that runs a turn reads its latest update. An idle one reads the end of its last finished
 * run, which a rename leaves alone though OpenCode stamps the rename as an update. OpenCode records
 * no end for a run that a quit or a crash cut off, so such a session reads the run before it, or,
 * with none, its latest update, which a rename does move. Its creation time would date it by when
 * it began rather than when it was last prompted.
 */
export function localActivityAt(
  session: Pick<LocalSessionSummary, 'active' | 'updatedAt' | 'idleAt'>,
): string {
  return isoTime(session.active ? session.updatedAt : (session.idleAt ?? session.updatedAt));
}

export function localStatus(session: {
  archived: boolean;
  active: boolean;
}): Extract<CodeSessionRow['status'], 'archived' | 'running' | 'idle'> {
  if (session.archived) {
    return 'archived';
  }
  return session.active ? 'running' : 'idle';
}

/**
 * A local session has no installation, repository, branch or sandbox. The
 * zero and empty values reach no cloud read: `capabilitiesOf` hides each
 * control a local session can reach that would make one, the parts only
 * cubic's sandbox produces (its boot, setup, suggestions, findings and
 * shared files) never appear here, and the controller skips its list and
 * environment writes for a local id.
 */
export function localSessionRow(input: {
  session: SessionInfo;
  turns: LocalTurnRow[];
  active: boolean;
  /** Whether a question or a permission request waits on the person. */
  awaitingInput: boolean;
}): CodeSessionRow {
  const { session } = input;
  const folder = folderName(session.location.directory);
  const firstTurn = input.turns[0];
  const updatedAt = isoTime(session.time.updated);
  return {
    id: session.id,
    installationId: 0,
    repositoryId: 0,
    repoFullName: folder,
    baseBranch: '',
    sessionBranch: '',
    sandboxName: '',
    openCodeSessionId: session.id,
    model: modelName(session),
    approvalMode: approvalModeOf(session.permissions),
    status: localStatus({ archived: archivedAt(session) !== null, active: input.active }),
    sandboxStatus: 'ready',
    rewindTurnId: localRewindTurnId(session.revert?.messageID),
    rewindRestoreFiles: true,
    checkInDueAt: null,
    forkedFromSessionId: null,
    sideAgentParentId: null,
    startedBySessionId: null,
    // The one person who writes here started it; the sidebar lists it under theirs.
    createdByProfileId: LOCAL_SESSION_AUTHOR_ID,
    source: 'web',
    sourceUrl: null,
    mcpClientName: null,
    title:
      session.title ??
      untitledSessionName(
        folder,
        firstTurn && codeSessionPromptTitle(firstTurn.prompt, firstTurn.promptAttachments),
      ),
    lastActivityAt: localActivityAt({
      active: input.active,
      updatedAt: session.time.updated,
      idleAt: session.time.idle ?? null,
    }),
    awaitingInputAt: input.awaitingInput ? updatedAt : null,
    updatedAt,
    turns: input.turns,
  };
}

/** The pieces of a `SessionResponse` that the session's event feed numbers. */
export type NumberedTail = { events: CodeSessionEvent[]; cursor: number };

/**
 * The session's `SessionResponse` from the client's snapshot, with the tail
 * of raw events already numbered for this page by the session's feed.
 */
export function localSessionResponse(
  snapshot: LocalSessionSnapshot,
  turns: LocalTurnRow[],
  tail: NumberedTail,
): SessionResponse {
  const forms = [...snapshot.forms, ...approvalForms(snapshot)];
  return {
    session: localSessionRow({
      session: snapshot.session,
      turns,
      active: snapshot.active,
      awaitingInput: forms.length > 0,
    }),
    transcript: {
      messages: snapshot.messages,
      inbox: snapshot.inbox,
      forms,
      cursor: null,
    },
    transcriptError: null,
    resuming: false,
    events: tail.events,
    cursor: tail.cursor,
    mcpToolsRunAs: null,
    imageRoom: localImageRoom(
      storedImageBase64Bytes(snapshot),
      turns.filter((turn) => turn.unsent),
    ),
    people: [
      {
        profileId: LOCAL_SESSION_AUTHOR_ID,
        name: 'You',
        login: null,
        avatarUrl: null,
        owner: true,
      },
    ],
  };
}
